import { VonqContractPostingRequirements } from 'api-clients/monolith/models/VonqContractPostingRequirements';
import { FormattedMessage } from 'react-intl';

import {
  LoadedOptions,
  shouldShowField,
} from './posting-requirements/display-rules';
import { useAutocompleteOptions } from './posting-requirements/use-autocomplete-options';

import { CircularProgress } from '@/components/ui/circular-progress';
import { TruncatedText } from '@/components/ui/truncated-text';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { cn } from '@/utils/classnames';
import CheckCircleIcon from '@/assets/icons/check-circle-solid.svg';

import { ChannelFormState } from '../types';
import { HIRING_ADDRESS_KEYS, isBlankAddressField } from './hiring-address';

export type SelectedSection = 'common' | string;

interface SideNavProps {
  openingId: string;
  contractRequirements: VonqContractPostingRequirements[];
  selectedSection: SelectedSection;
  onSelectSection: (section: SelectedSection) => void;
  isInVonqChannel: boolean;
  /** True when ZipRecruiter is selected and the opening is remote — stateCode is required in that case. */
  isStateCodeRequired: boolean;
  /** True for a remote opening, whose hiring address replaces the Location row in the Vonq-required set. */
  isRemote: boolean;
}

// Fields that are always required for every opening.
const ALWAYS_REQUIRED_KEYS = ['title', 'description'] as const;

// Additional fields required only when the opening is posted to a Vonq channel.
const VONQ_REQUIRED_KEYS = [
  'industry',
  'jobHours',
  'jobType',
  'experience',
  'locationAddress',
  'postalCode',
  'city',
] as const;

function countTotalRequiredFields(
  contract: VonqContractPostingRequirements,
  contractValues: Record<string, string | string[]>,
  loadedOptions?: LoadedOptions,
): number {
  return contract.posting_requirements.filter(
    f =>
      f.required &&
      f.type !== 'STATISCH' &&
      f.type !== 'QUESTIONNAIRE' &&
      shouldShowField(
        f,
        contractValues,
        contract.posting_requirements,
        loadedOptions,
      ),
  ).length;
}

function countIncompleteRequiredFields(
  contract: VonqContractPostingRequirements,
  vonqValues: Record<string, Record<string, string | string[]>>,
  contractErrors: Record<string, unknown>,
  loadedOptions?: LoadedOptions,
): number {
  const contractValues = vonqValues[contract.contract_id] ?? {};
  return contract.posting_requirements.filter(field => {
    if (!field.required) return false;
    if (field.type === 'STATISCH' || field.type === 'QUESTIONNAIRE')
      return false;
    if (
      !shouldShowField(
        field,
        contractValues,
        contract.posting_requirements,
        loadedOptions,
      )
    )
      return false;
    const value = contractValues[field.name];
    const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
    const hasError = !!contractErrors[field.name];
    return isEmpty || hasError;
  }).length;
}

export function SideNav({
  openingId,
  contractRequirements,
  selectedSection,
  onSelectSection,
  isInVonqChannel,
  isStateCodeRequired,
  isRemote,
}: SideNavProps) {
  const { hookForm } = useJobPostingContext();
  const {
    watch,
    formState: { errors },
  } = hookForm;

  const channels = watch('channels');
  const vonqValues =
    watch(`jobDetails.${openingId}.vonqPostingRequirements`) ?? {};

  const totalBoardCount = Object.keys(channels ?? {}).length;

  const vonqRequiredKeys = VONQ_REQUIRED_KEYS.map(key =>
    isRemote && key === 'locationAddress' ? 'streetAddress' : key,
  );
  const requiredCommonKeys = [
    ...ALWAYS_REQUIRED_KEYS,
    ...(isInVonqChannel ? vonqRequiredKeys : []),
    ...(isStateCodeRequired ? (['stateCode'] as const) : []),
  ];
  const totalCommonRequired = requiredCommonKeys.length;

  const jobDetailErrors = errors?.jobDetails?.[openingId];
  // A remote opening's address fields count by value, as useOpeningProgress does.
  const hiringAddress = watch([
    `jobDetails.${openingId}.streetAddress`,
    `jobDetails.${openingId}.city`,
    `jobDetails.${openingId}.postalCode`,
  ]);
  const commonGapCount = requiredCommonKeys.filter(key => {
    const addressIndex = isRemote
      ? (HIRING_ADDRESS_KEYS as readonly string[]).indexOf(key)
      : -1;
    return addressIndex >= 0
      ? isBlankAddressField(hiringAddress[addressIndex])
      : !!jobDetailErrors?.[key];
  }).length;
  const commonCompleteCount = totalCommonRequired - commonGapCount;
  const commonProgress = (commonCompleteCount / totalCommonRequired) * 100;

  const channelsWithContracts = Object.entries(channels ?? {}).filter(
    ([, channel]) => {
      const contractId = channel.vonqDetails?.contractId;
      if (contractId == null) return false;
      const contract = contractRequirements.find(
        r => r.contract_id === contractId,
      );
      if (!contract) return false;
      const contractValues = vonqValues[contract.contract_id] ?? {};
      return countTotalRequiredFields(contract, contractValues) > 0;
    },
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-2">
        <Typography variant="caption" className="text-fx-neutral-400">
          <FormattedMessage
            defaultMessage="Fields by Board"
            description="Left panel heading for the 'Fields by Board' navigation in the expanded job card"
          />
        </Typography>

        <NavItem
          isSelected={selectedSection === 'common'}
          progress={commonProgress}
          label={
            <FormattedMessage
              defaultMessage="Common fields"
              description="Nav item label for common fields shared across all boards"
            />
          }
          subtitle={
            commonCompleteCount === totalCommonRequired ? (
              <FormattedMessage
                defaultMessage="All {count} boards"
                description="Subtitle showing how many boards share these common fields"
                values={{ count: totalBoardCount }}
              />
            ) : (
              <FormattedMessage
                defaultMessage="{count} to fill"
                description="Subtitle showing how many common fields remain to be filled"
                values={{
                  count: totalCommonRequired - commonCompleteCount,
                }}
              />
            )
          }
          onClick={() => onSelectSection('common')}
        />
      </div>

      {channelsWithContracts.length > 0 && (
        <div className="flex flex-col gap-2">
          <Typography variant="caption" className="text-fx-neutral-400">
            <FormattedMessage
              defaultMessage="Board Specific"
              description="Left panel sub-heading for board-specific posting requirement fields"
            />
          </Typography>

          {channelsWithContracts.map(([channelKey, channel]) => (
            <ContractNavItem
              key={channelKey}
              channelKey={channelKey}
              channel={channel}
              contractRequirements={contractRequirements}
              vonqValues={vonqValues}
              jobDetailErrors={jobDetailErrors}
              isSelected={selectedSection === channelKey}
              onSelect={onSelectSection}
            />
          ))}
        </div>
      )}

      <div className="bg-fx-neutral-100 flex flex-col gap-1 rounded-lg p-2">
        <Typography variant="small1" className="text-fx-neutral-500">
          <FormattedMessage
            defaultMessage="<bold>Contract boards</bold> each ask for extra inputs unique to that board's requirements."
            description="Explanation that contract boards each require their own unique set of fields"
            values={{
              bold: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
            }}
          />
        </Typography>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function ContractNavItem({
  channelKey,
  channel,
  contractRequirements,
  vonqValues,
  jobDetailErrors,
  isSelected,
  onSelect,
}: {
  channelKey: string;
  channel: ChannelFormState;
  contractRequirements: VonqContractPostingRequirements[];
  vonqValues: Record<string, Record<string, string | string[]>>;
  jobDetailErrors: Record<string, unknown> | undefined;
  isSelected: boolean;
  onSelect: (section: SelectedSection) => void;
}) {
  const contract = contractRequirements.find(
    r => r.contract_id === channel.vonqDetails?.contractId,
  );
  // Hook must be called unconditionally — pass undefined when not found.
  const loadedOptions = useAutocompleteOptions(contract);

  if (!contract) return null;

  const contractValues = vonqValues[contract.contract_id] ?? {};
  const contractErrors =
    (
      jobDetailErrors?.vonqPostingRequirements as
        | Record<string, Record<string, unknown>>
        | undefined
    )?.[contract.contract_id] ?? {};
  const total = countTotalRequiredFields(
    contract,
    contractValues,
    loadedOptions,
  );
  const incompleteCount = countIncompleteRequiredFields(
    contract,
    vonqValues,
    contractErrors,
    loadedOptions,
  );
  const progress = total > 0 ? ((total - incompleteCount) / total) * 100 : 100;

  return (
    <NavItem
      isSelected={isSelected}
      progress={progress}
      label={channel.displayTitle}
      subtitle={
        incompleteCount > 0 ? (
          <FormattedMessage
            defaultMessage="{count} to fill"
            description="Count of required posting requirement fields remaining for this board"
            values={{ count: incompleteCount }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="Contract"
            description="Subtitle shown when all board-specific required fields are filled"
          />
        )
      }
      onClick={() => onSelect(channelKey)}
    />
  );
}

function NavItem({
  isSelected,
  progress,
  label,
  subtitle,
  onClick,
}: {
  isSelected: boolean;
  progress: number;
  label: React.ReactNode;
  subtitle: React.ReactNode;
  onClick: () => void;
}) {
  const isComplete = progress >= 100;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg p-2 text-left transition-colors',
        isSelected ? 'bg-fx-brand-50' : 'hover:bg-fx-neutral-50',
      )}
    >
      {isComplete ? (
        <img
          src={CheckCircleIcon}
          alt=""
          className="text-fx-green h-5 w-5 shrink-0"
        />
      ) : (
        <CircularProgress value={progress} />
      )}

      <div className="flex min-w-0 flex-col">
        <TruncatedText variant="body1" as="span">
          {label}
        </TruncatedText>
        <TruncatedText
          variant="small1"
          as="span"
          className={isComplete ? 'text-fx-neutral-400' : 'text-fx-amber'}
        >
          {subtitle}
        </TruncatedText>
      </div>
    </button>
  );
}
